import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { AgentVersionConfigurationInput } from "src/agents/types";
import { AgentModelInfo, Model } from "src/clients/agent-management";
import { AWSRegion } from "src/constants/AwsRegions";
import {
  ModelOption,
  ModelSelectorDropdown,
  PROVIDER_BADGE_CONFIG,
} from "src/genai/ModelSelector";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type AgentModelSelectorProps = {
  /** Whether the selector is disabled (e.g., for published versions) */
  disabled?: boolean;
  /** Available models adapted for the dropdown */
  modelOptions: ModelOption[];
};

/**
 * Model selector component for agents.
 * Models are passed down from the parent component which fetches them.
 */
export const AgentModelSelector: React.FC<AgentModelSelectorProps> = ({
  disabled = false,
  modelOptions,
}) => {
  const { control } = useFormContext<AgentVersionConfigurationInput>();
  const { workspace } = useWorkspaceAndOrg();
  const workspaceRegionAWS = workspace?.aws_region as AWSRegion | undefined;

  return (
    <Controller
      control={control}
      name="model_config.model"
      render={({ field }) => (
        <ModelSelectorDropdown
          dataLoc="agent-model-selector"
          disabled={disabled}
          modelOptions={modelOptions}
          selectedModelId={field.value}
          showDescription={false}
          workspaceRegionAWS={workspaceRegionAWS}
          onSelect={(modelId) => field.onChange(modelId as Model)}
        />
      )}
    />
  );
};

export const convertAgentModelInfoToModelOption = (
  model: AgentModelInfo,
): ModelOption => ({
  id: model.model_id,
  displayName: model.display_name,
  description: undefined, // Agents API doesn't provide description (yet)
  provider: model.provider,
  disabled: false, // Agents API only returns available models
  disabledReason: undefined,
  badgeConfig: PROVIDER_BADGE_CONFIG[model.provider],
});
